<template>
    <div class="header-bar">
        <div class="header-bar-content">
            <div
                class="left-text-wrapper"
                :style="{ 'min-width': leftTextWrapperMinWidth }"
            >
                <span class="header-bar-title">{{ vacancyName }}</span>

                <div class="header-bar-title with-icon">
                    {{ truncatedBriefing }}

                    <el-button
                        type="text"
                        icon="el-icon-more"
                        @click="showBriefingModal = true"
                        class="briefing-button"
                    >
                        <img
                            src="/media/images/drupal_reference_icon.png"
                            alt="Reference Icon"
                            class="briefing-icon"
                        />
                    </el-button>
                </div>
            </div>

            <div class="middle-sections-wrapper">
                <div class="profile-section">
                    <span class="header-bar-center">{{
                        $trans("profile_dossier.candidate") || "Candidate"
                    }}</span>

                    <div class="profile-wrapper">
                        <img
                            :src="candidateData.avatar"
                            alt="Candidate Avatar"
                            class="profile-avatar"
                        />
                        <span class="profile-name">{{
                            candidateData.name
                        }}</span>
                    </div>
                </div>

                <div class="profile-section">
                    <span class="header-bar-center">{{
                        $trans("profile_dossier.supporting_consultant")
                    }}</span>

                    <div class="profile-wrapper">
                        <img
                            :src="consultantData.consultant_avatar"
                            alt="Consultant Avatar"
                            class="profile-avatar"
                        />

                        <span class="profile-name">{{
                            consultantData.consultant_name
                        }}</span>

                        <div class="consultant-buttons">
                            <el-button
                                class="custom-icon-button"
                                @click="showCalendlyModal = true"
                            >
                                <img
                                    src="/media/images/calendar.svg"
                                    alt="Calendar"
                                />
                            </el-button>

                            <el-button
                                class="custom-icon-button"
                                @click="showEmailModal = true"
                            >
                                <img
                                    src="/media/images/send_email.svg"
                                    alt="Email"
                                />
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>

            <button
                class="header-bar-update-btn"
                v-if="hasUpdates && !readOnly"
                :disabled="isUpdating"
                @click="$emit('applyUpdates')"
            >
                {{ isUpdating ? 'Updating...' : 'Update' }}
            </button>

            <button
                class="header-bar-remove-button"
                v-if="!hidePdfBtn && !readOnly"
                @click="showDeleteConfirmModal = true"
            >
                <el-icon color="white">
                    <DeleteIcon />
                </el-icon>
            </button>

            <button
                class="header-bar-button"
                style="margin-right: 10px"
                v-if="!hidePdfBtn"
                @click="$emit('openPdf')"
            >
                PDF
            </button>

            <button
                class="header-bar-button"
                @click="$emit('save')"
                v-if="!readOnly"
            >
                {{ saveButtonText }}
            </button>
        </div>
        <el-dialog
            v-model="showBriefingModal"
            title="Manager Briefing"
            width="50%"
            :top="'10px'"
        >
            <div v-html="briefing" style="white-space: normal"></div>
            <template #footer>
                <el-button @click="showBriefingModal = false">Close</el-button>
            </template>
        </el-dialog>
    </div>
    <CalendlyModal
        v-if="showCalendlyModal"
        v-model="showCalendlyModal"
        :calendly-url="consultantData?.consultant_calendy_url"
    />
    <SendConsultantEmailModal
        v-model="showEmailModal"
        :consultant-email="consultantData.consultant_email"
        :consultant-name="consultantData.consultant_name"
        :consultant-avatar="consultantData.consultant_avatar"
        :consultant-title="consultantData.consultant_title"
        :consultant-mobile="consultantData.consultant_mobile"
        :sender-email="senderEmail"
        :sender-id="senderId"
        :profile-id="profileId"
        :vacancy-id="vacancyData.id"
        :api-route="consultantData.url_send_email"
    />

    <DeleteDossierModal
        v-model="showDeleteConfirmModal"
        @confirm="handleDelete"
    />
</template>

<script>
import CalendlyModal from "./CalendlyModal.vue";
import SendConsultantEmailModal from "./SendConsultantEmailModal.vue";
import DeleteDossierModal from "./DeleteDossierModal.vue";

import { Delete as DeleteIcon } from "@element-plus/icons-vue";

export default {
    name: "HeaderBar",
    components: {
        CalendlyModal,
        SendConsultantEmailModal,
        DeleteIcon,
        DeleteDossierModal,
    },
    props: {
        vacancyData: {
            type: Object,
            required: true,
        },
        consultantData: {
            type: Object,
            required: true,
        },
        candidateData: {
            type: Object,
            required: true,
        },
        senderId: {
            type: Number,
            required: true,
        },
        senderEmail: {
            type: String,
            required: true,
        },
        profileId: {
            type: Number,
            required: true,
        },
        saveButtonText: {
            type: String,
            default: "save",
        },
        leftTextWrapperMinWidth: {
            type: String,
            default: "550px",
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        hidePdfBtn: {
            type: Boolean,
            default: false,
        },
        deleteUrl: {
            type: String,
            required: true,
        },
        hasUpdates: {
            type: Boolean,
            default: false,
        },
        isUpdating: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showBriefingModal: false,
            showCalendlyModal: false,
            showEmailModal: false,
            showDeleteConfirmModal: false,
        };
    },
    computed: {
        vacancyName() {
            return this.vacancyData?.name_4_client || "Vacancy Name";
        },
        briefing() {
            return this.vacancyData?.manager_briefing || "";
        },
        truncatedBriefing() {
            const div = document.createElement("div");
            div.innerHTML = this.briefing;
            const text = div.textContent || div.innerText || "";
            return text.length > 30 ? text.slice(0, 60) + "..." : text;
        },
        consultant() {
            return this.vacancyData?.consultant_name || "";
        },
    },
    methods: {
        handleDelete() {
            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content");

            this.$functions.showLoader();

            fetch(this.deleteUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRF-TOKEN": csrfToken,
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                    this.$message.error(
                        "Could not delete the dossier. Please try again.",
                    );
                })
                .then((data) => {
                    this.$message.success(data.message);
                    window.location.href = data.redirect_url;
                })
                .catch((error) => {
                    this.$message.error(
                        "Could not delete the dossier. Please try again.",
                    );
                })
                .finally(() => {
                    this.$functions.hideLoader();
                });
        },
    },
};
</script>

<style scoped>
.header-bar {
    position: sticky;
    top: 80px;
    z-index: 9;
    background: white;
    border-bottom: 1px solid #bcbab6;
}

.header-bar-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
}

.header-bar-title {
    font-weight: 500;
    font-size: 16px;
}

.header-bar-button {
    background-color: #41abc4;
    color: white;
    padding: 8px 38px;
    border: none;
    cursor: pointer;
    font-weight: 600;
}

.header-bar-button:hover {
    background-color: #2699b2;
}

.left-text-wrapper {
    display: grid;
    min-width: 550px;
}

.middle-sections-wrapper {
    flex: 1;
    display: flex;
    gap: 50px;
}

.profile-section {
    display: grid;
    text-align: left;
    align-self: center;
}

.header-bar-center {
    font-size: 18px;
    font-weight: 600;
}

.profile-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.profile-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid #ccc;
}

.profile-name {
    font-weight: 500;
    font-size: 16px;
}

.consultant-buttons {
    display: flex;
    gap: 8px;
}

.icon-button img {
    width: 20px;
    height: 20px;
}

.custom-icon-button {
    background-color: #41abc4;
    border-radius: 8px;
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.3s ease;
}

.custom-icon-button:hover {
    background-color: #2699b2;
}

.custom-icon-button img {
    width: 29px;
    height: 30px;
    filter: brightness(0) invert(1);
}

.with-icon {
    display: flex;
    align-items: center;
    gap: 5px;
}

.briefing-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
}

.briefing-button {
    margin-left: -20px;
}

.el-button {
    border: unset !important;
}

.el-icon {
    width: 22px;
    height: 22px;
}

.el-icon {
    background-color: #41abc4;
    padding: 10px;
    width: 18px;
    height: 12px;
    background-image: linear-gradient(#ff6b65 0 0);
    background-size: 100% 0em;
    background-repeat: no-repeat;
    transition: 0.1s;
}

.el-icon:hover {
    transition: 0.1s;
    background-size: 100% 100%;
    cursor: pointer;
}

.el-icon > svg {
    width: 22px;
    height: 22px;
}

.header-bar-remove-button {
    background-color: transparent;
    border: unset;
    margin-right: 7px;
}

.header-bar-update-btn {
    background-color: #41abc4;
    color: white;
    padding: 8px 38px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    margin-right: 10px;
}

.header-bar-update-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>